import React from "react";
import axios from "axios";
import ConfigParser from "../../../js/common/utility/configParser";

import Banner from "./components/Banner";
import Counter from "./components/Counter";
import Modal from "./components/Modal";

import { fetchDrawing, createAttendee, fetchAttendees } from "./util";

import "./index.scss";

const FETCH_INTERVAL_IN_SECOND = 7 * 24 * 3600;

export default class Drawing extends React.Component {

    constructor(props) {
        super(props);

        // Creating config file from parsing xml file
        const { config } = this.props;
        const parser = ConfigParser.toParser(config);

        const parsedConfig = {
            props: ConfigParser.parseRoot(config, "pubhub-drawing"),
            drawingId: parser.parseString("drawingId", "")
        };

        this.defaultAnswers = {
            none: {
                question_id: "",
                data: undefined
            },
            guess_number: {
                question_id: "",
                data: ""
            },
            free_form: {
                question_id: "",
                data: ""
            },
            multiple_choice: {
                question_id: "",
                data: []
            }
        };

        // Default initial state, assigning config file to state
        this.state = {
            config: parsedConfig,
            data: undefined,
            answers: [],
            countdown: {
                hours: 0,
                minutes: 0,
                seconds: 0
            },
            isLoading: false,
            isWidgetShown: false,
            isModalShown: false,
            isEntered: undefined,
            isTermsAgreed: false,
            isDrawingEnded: false
        };

        // Event handlers
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleLogin = this.handleLogin.bind(this);
        this.handleEnterDrawing = this.handleEnterDrawing.bind(this);
        this.handleToggleModal = this.handleToggleModal.bind(this);
        this.handleWinnersRedirect = this.handleWinnersRedirect.bind(this);
    }

    afterLogin = () => {
        this.setState({ isTermsAgreed: true });
        this.handleToggleModal();
        setTimeout(() => {
            window.PubHub.util.scrollTo(".pubhub-drawing", 300, 86);
        }, 1000);
    }

    calculateTimeRemaining(end, tz) {
        const isMomentLoaded = window["moment"] && window["moment"]["tz"];

        if (!isMomentLoaded) return {
            countdown: this.state.countdown,
            isDrawingEnded: this.state.isDrawingEnded
        };

        const now = moment();
        const tzNow = moment.tz(now, tz);
        const tzEnd = moment.tz(end, tz);
        const hours = Math.max(tzEnd.diff(tzNow, "hours"), 0);
        const minutes = Math.max(tzEnd.diff(tzNow, "minutes") - (hours * 60), 0);
        const seconds = Math.max(tzEnd.diff(tzNow, "seconds") - (hours * 60 * 60) - (minutes * 60), 0);
        const countdown = { hours, minutes, seconds };
        const isDrawingEnded = Object.values(countdown).every(el => el <= 0);

        isDrawingEnded && clearInterval(this.countdownTimer);

        return { countdown, isDrawingEnded };
    }

    constantTimeEquals(a = "", b = "") {
        const left = String(a).toLowerCase();
        const right = String(b).toLowerCase();
        const maxLength = Math.max(left.length, right.length);
        let diff = left.length ^ right.length;

        for (let i = 0; i < maxLength; i++) {
            const leftCode = i < left.length ? left.charCodeAt(i) : 0;
            const rightCode = i < right.length ? right.charCodeAt(i) : 0;
            diff |= (leftCode ^ rightCode);
        }

        return diff === 0;
    }

    isEntryCompleted() {
        const { isEntered, isDrawingEnded } = this.state;

        return isEntered || isDrawingEnded;
    }

    handleInputChange({ target: { name, value, checked, type } }) {
        const setting = type === "checkbox" ? checked : value;

        this.setState({
            [name]: setting
        });
    }

    handleAnswerChange = ({ target: { name, value } }, index) => {
        const getMultipleChoiceSubmission = () => (
            answers[index].data.includes(value)
                ? answers[index].data.filter(item => item !== value)
                : [...answers[index].data, value]
        );

        const { answers } = this.state;

        const answerData = (
            name === "free_form"
                ? value
                : name === "guess_number"
                    ? Number.parseFloat(value) || ""
                    : name === "multiple_choice"
                        ? getMultipleChoiceSubmission()
                        : undefined
        );

        const updatedAnswer = {
            ...answers[index],
            data: answerData
        };

        const updatedAnswers = [
            ...answers.slice(0, index),
            updatedAnswer,
            ...answers.slice(index + 1)
        ];

        this.setState({
            answers: updatedAnswers
        });
    }

    handleLogin() {
        window.PlatformSDK.openProtectedData("enter-drawing");
    }

    handleEnterDrawing() {
        const {
            answers,
            config: { drawingId }
        } = this.state;

        // Call update profile survey, answer validation is done SDK-side instead of client-side.
        this.state.data.profile_question && window.PlatformSDK.updateProfileSurvey();

        const waitForRetry = (wait) => Infinity; // wait * 2: reasonable retry increment; Infinity: no more retry
        const retry = (wait) => {
            // Add the user as an attendee to this drawing and hide the modal.
            createAttendee(drawingId, answers)
                .then(() => {
                    this.setState({
                        isEntered: true,
                        isModalShown: false
                    });
                })
                .catch(error => {
                    if (isFinite(wait)) {
                        setTimeout(() => {
                            retry(waitForRetry(wait));
                        }, wait);
                    } else {
                        // TODO report to the user
                        console.error("Fail to enter the drawing", error);
                    }
                });
        };
        retry(1000);
    }

    handleToggleModal() {
        this.setState({
            isModalShown: !this.state.isModalShown
        });
    }

    handleWinnersRedirect() {
        const { drawingId } = this.state.config;
        window.location.href = `https://developer.cisco.com/drawing/${drawingId}/winners`;
    }

    handleCountdown = (() => {
        var data = null;
        var last = 0;
        var fetching = false;
        const fetch = () => {
            if (!fetching) {
                fetching = true;
                fetchDrawing(this.state.config.drawingId)
                    .then(res => {
                        data = res.data;
                        countdown();
                        fetching = false;
                    });
            }
        };
        const countdown = () => {
            if (!data) {
                fetch();
                return;
            }
            const { end, timezone } = data;
            const tz = timezone.split("#")[1];
            const { countdown, isDrawingEnded } = this.calculateTimeRemaining(end, tz);
            this.setState({ countdown, isDrawingEnded });
        };
        const handler = () => {
            const now = (new Date()).getTime();
            if (!last || now - last > FETCH_INTERVAL_IN_SECOND * 1000) {
                fetch();
                last = now;
            } else {
                countdown();
            }
        };
        return handler;
    })()

    componentDidMount() {
        const { drawingId } = this.state.config;

        PubHub.util.loadJSFile("https://momentjs.com/downloads/moment.min.js");
        PubHub.util.loadJSFile("https://momentjs.com/downloads/moment-timezone-with-data-10-year-range.min.js");

        window.PlatformSDK.resumeProtectedData(cookieValue => {
            /**
             * Guard; if the cookie's value isn't equal to the one we set earlier then
             * return true to indicate that we shouldn't delete the existing cookie.
             */
            if (cookieValue !== "enter-drawing") return true;

            this.afterLogin();
        });

        this.setState({
            isLoading: true
        });

        // Fetch desired drawing.
        axios.all([
            fetchDrawing(drawingId),
            fetchAttendees(drawingId)
        ]).then(axios.spread(({ data }, attendees) => {
            const { end, timezone, questions, widget_visibility, secret_url_path } = data;
            const tz = timezone.split("#")[1];
            const { countdown, isDrawingEnded } = this.calculateTimeRemaining(end, tz);

            // Show this widget if it's set as "visible" and the secret matches if one is set.
            const isWidgetShown = (
                widget_visibility
                && (
                    secret_url_path
                        ? this.constantTimeEquals(secret_url_path, window.location.hash.substr(1))
                        : true
                )
            );

            const isEntered = (
                window.PlatformSDK.isLoggedIn()
                && attendees.data.some(attendee => attendee.profile_id === window.PlatformSDK.profileID)
            );

            const answers = [0, 1].map(index => {
                const { id, type } = questions[index];

                return {
                    ...this.defaultAnswers[type],
                    question_id: id
                };
            });

            this.setState({
                data,
                answers,
                countdown,
                isLoading: false,
                isWidgetShown,
                isEntered,
                isDrawingEnded
            });

            // Set the countdown timer running if the drawing hasn't already ended and widget is shown.
            const shouldCountdown = !isDrawingEnded && isWidgetShown;

            // TO-DO: setTimeout.
            this.countdownTimer = shouldCountdown && setInterval(this.handleCountdown, 1000);
        }));
    }

    componentDidUpdate(prevProps, prevState) {
        const { isWidgetShown } = this.state;

        if (isWidgetShown === prevState.isWidgetShown) return;

        const existingMarginTop = Number.parseFloat(window.getComputedStyle(document.body).marginTop);
        const bannerHeight = Number.parseFloat(document.getElementById("pubhub-drawing-banner").offsetHeight);

        document.body.style.marginTop = `${isWidgetShown ? existingMarginTop + bannerHeight : existingMarginTop}px`;
        isWidgetShown && window.PlatformSDK.utils.emit("drawing-widget-shown");
    }

    // Render content
    renderContent() {
        const {
            countdown,
            data: {
                title,
                description,
                promotion_rule
            },
            isEntered,
            isDrawingEnded,
            isTermsAgreed
        } = this.state;

        const isEntryCompleted = this.isEntryCompleted();

        const termsLbl = (
            isEntryCompleted
                ? (
                    <label className="terms-text">
                        Winner will be notified after contest ends, see <a href={promotion_rule}>Official Promotion Rules</a> for more details.
                    </label>
                )
                : (
                    <div className="d-flex justify-content-center counter-same-width">
                        <label className='terms-checkbox-box'>
                            <div
                                className="terms-text"
                            >
                                I have read and agree to the <a href="https://developer.cisco.com/site/license/terms-and-conditions/">Cisco DevNet Terms of Service</a>, the <a href="https://www.cisco.com/c/en/us/about/legal/privacy.html">Cisco Online Privacy Policy</a>, and the <a href={promotion_rule}>Official Promotion Rules</a>.
                        </div>

                            <input
                                id="js-isTermsAgreed"
                                className="terms-checkbox"
                                type="checkbox"
                                name="isTermsAgreed"
                                checked={isTermsAgreed}
                                onChange={this.handleInputChange}
                            />
                            <span className='terms-checkbox-checkmark'></span>
                        </label>
                    </div>
                )
        )

        const enteredMsg = (
            isEntered
                ? (
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-6">
                            <div className="entered-container d-flex align-items-center justify-content-center">
                                <div className="entered-icon fas fa-check"></div>

                                <div className="entered-text">Successfully entered!</div>
                            </div>
                        </div>
                    </div>
                )
                : undefined
        );

        const submitBtn = {
            text: (
                window.PlatformSDK.isLoggedIn()
                    ? "Enter drawing"
                    : "Login to enter drawing"
            ),
            class: isTermsAgreed ? "is-active" : "is-inactive",
            disabled: !isTermsAgreed,
            handleClick: (
                // Terms are agreed and user is logged in, open entry modal.
                isTermsAgreed && window.PlatformSDK.isLoggedIn()
                    ? this.handleToggleModal

                    // Terms are agreed but user isn't logged in, open login modal which will then open entry modal.
                    : isTermsAgreed && !window.PlatformSDK.isLoggedIn()
                        ? this.handleLogin
                        : undefined
            )
        };

        return (
            <div className="inner-contents">
                <div className="row justify-content-center">
                    <div className="col-8 col-md-6 col-lg-8 d-flex justify-content-center">
                        <div className="gift-icon fas fa-gift"></div>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-12 col-sm-9 col-md-6 col-lg-8">
                        <div className="title">{title}</div>
                    </div>
                </div>

                <div className="row justify-content-center">
                    <div className="col-12 col-md-6 col-lg-8">
                        <div className="description">{description}</div>
                    </div>
                </div>

                <Counter
                    countdown={countdown}
                />

                <div className="row justify-content-center">
                    <div className="col-12 col-md-6 col-lg-8 d-flex justify-content-center">
                        {termsLbl}
                    </div>
                </div>

                {enteredMsg}

                <div className="row justify-content-center">
                    <div className="col-12 col-md-4">
                        {
                            !isEntered
                            && !isDrawingEnded
                            && (
                                <button
                                    className={`submit-btn ${submitBtn.class} btn btn-lg btn-block`}
                                    disabled={submitBtn.disabled}
                                    onClick={submitBtn.handleClick}
                                >
                                    {submitBtn.text}
                                </button>
                            )
                        }
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            config,
            data,
            answers,
            countdown,
            isLoading,
            isWidgetShown,
            isModalShown,
            isEntered,
            isDrawingEnded
        } = this.state;

        // Widget shouldn't be visible, return without rendering anything.
        if (isLoading || !data || (!isLoading && !isWidgetShown)) return <div></div>;

        return (
            <div {...config.props}>
                <Banner
                    countdown={countdown}
                    isLoading={isLoading}
                    isEntered={isEntered}
                    isDrawingEnded={isDrawingEnded}
                    handleWinnersRedirect={this.handleWinnersRedirect}
                />

                <div className="abstract-left"></div>

                <div className="abstract-right"></div>

                <div className="container">
                    {this.renderContent()}
                </div>

                {
                    data
                    && isEntered === false
                    && <Modal
                        data={data}
                        answers={answers}
                        isShown={isModalShown}
                        handleAnswerChange={this.handleAnswerChange}
                        handleEnterDrawing={this.handleEnterDrawing}
                        handleToggleModal={this.handleToggleModal}
                    />
                }
            </div>
        );
    }
}
